import type { ValidationRules } from '@commenda/types';
import type { PreviewFillOutcome } from '../../hooks/FormContext';
import {
  type AutoDetectFieldInfo,
  getAutoDetectFieldTestValue,
} from '../../preview/test-value-registry';
import { isValueEmpty } from '../../utils/form-state';

interface AutoDetectFieldPreviewFillArgs {
  fieldId: string;
  currentValue: unknown;
  isWaitingOnContext: boolean;
  isLoading: boolean;
  isReadOnly: boolean;
  fieldInfo: AutoDetectFieldInfo | null | undefined;
  rules?: ValidationRules;
  updateField: (fieldId: string, value: unknown) => void;
  validateField: (
    fieldId: string,
    value: unknown,
    rules?: ValidationRules
  ) => string | null;
}

/**
 * Preview fill for an AutoDetectField. Writes a test value only once the
 * field's schema type has resolved and it is empty, editable and in context.
 */
export function fillAutoDetectFieldForPreview({
  fieldId,
  currentValue,
  isWaitingOnContext,
  isLoading,
  isReadOnly,
  fieldInfo,
  rules,
  updateField,
  validateField,
}: AutoDetectFieldPreviewFillArgs): PreviewFillOutcome {
  if (!isValueEmpty(currentValue)) return { kind: 'already-filled' };
  if (isWaitingOnContext) {
    return { kind: 'skipped', reason: 'disabled — waiting for context' };
  }
  if (isLoading) return { kind: 'loading' };
  if (isReadOnly) return { kind: 'skipped', reason: 'read-only field' };
  if (!fieldInfo) {
    return { kind: 'skipped', reason: 'field type could not be resolved' };
  }
  const result = getAutoDetectFieldTestValue(fieldInfo, rules);
  if (result.kind === 'skip') {
    return { kind: 'skipped', reason: result.reason };
  }
  updateField(fieldId, result.value);
  validateField(fieldId, result.value, rules);
  return { kind: 'filled' };
}
